import styled from '@emotion/styled';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { resistanceLevelMetadata } from '@resistapp/client/data-utils/resistance-level';
import { L2Targets } from '@resistapp/common/assays';
import { capitalizeOnlyWords, friendlyL2Target } from '@resistapp/common/friendly';
import { References, useScienceReference } from '../../../hooks/use-science-reference';
import { oldTheme } from '../../shared/old-styles';
import { MetricTitle } from '../../shared/overview-metric-title';
import { LevelInfo } from './level-info';

export function IndexInfo() {
  const { selectedAntibiotic, metricGeneCnt, isOneHealthProject } = useOverviewContext();

  const indexHeight = 420;
  const acronym = !isOneHealthProject && !selectedAntibiotic ? 'ARGI' : '';

  // Get a friendly name for the antibiotic if one is selected
  const friendlyAntibiotic = selectedAntibiotic ? friendlyL2Target(selectedAntibiotic as L2Targets) : null;

  // Create dynamic panel description
  const getPanelDescription = () => {
    const baseDescription = isOneHealthProject
      ? `a standardised set of ${metricGeneCnt} highly relevant`
      : `a custom set of ${metricGeneCnt}`;

    // If a specific antibiotic is selected
    if (friendlyAntibiotic) {
      return `${baseDescription} ${friendlyAntibiotic} resistance genes in ${isOneHealthProject ? "Resistomap's OneHealth Panel, making it globally comparable across sampling projects" : 'the panel'}`;
    }

    // Default description for all ARGs
    return `${baseDescription} ARGs in ${isOneHealthProject ? "Resistomap's OneHealth Panel, making it globally comparable across sampling projects" : 'the panel'}`;
  };

  const [Ref1, Ref1Num] = useScienceReference({
    number: '1',
    href: 'https://doi.org/10.1016/j.envint.2023.108084',
    text: 'A. Abramova et. al., 2023, A global baseline for qPCR-determined antimicrobial…',
  });

  return (
    <div>
      <InfoSection>
        <div>
          <i>
            <MetricTitle noColor noBold />
            {acronym ? ` (${acronym}) ` : ' '}
          </i>
          shows the {friendlyAntibiotic ? `${friendlyAntibiotic} resistance gene` : 'overall ARG'} load in the microbial
          community on a three level scale.
        </div>
      </InfoSection>
      <InfoSection>
        <b>Scope of genes</b>
        <div style={{ marginTop: 4 }}>The index considers {getPanelDescription()}.</div>
      </InfoSection>
      <InfoSection>
        <b>Metric calculation</b>
        <MathIntend>
          <code>
            ARGI&nbsp;=&nbsp;5&nbsp;+&nbsp;mean(log<sub>10</sub>(r<sub>a</sub>)),&nbsp;ARGI
            &nbsp;&isin;&nbsp;[0,&nbsp;5]
          </code>
        </MathIntend>
        where{' '}
        <code>
          r<sub>a</sub>
        </code>{' '}
        is the relative abundance of ARGs (normalised to the abundance of the 16S&nbsp;rRNA&nbsp;gene).
      </InfoSection>

      <InfoSection>
        <b>Biological meaning</b>
        <div style={{ marginTop: 4 }}>
          The range is divided into three levels: <i>{capitalizeOnlyWords(resistanceLevelMetadata.low.title)}</i>,{' '}
          <i>{capitalizeOnlyWords(resistanceLevelMetadata.moderate.title)}</i>, and{' '}
          <i>{capitalizeOnlyWords(resistanceLevelMetadata.high.title)}</i>. The below graph illustrates the different
          levels and their biological meaning:
        </div>
      </InfoSection>
      <LevelInfo indexHeight={indexHeight} Ref1Num={Ref1Num} />
      <InfoSection>
        <References style={{ marginTop: 60 }}>
          <Ref1 />
        </References>
      </InfoSection>
    </div>
  );
}

const InfoSection = styled.div`
  padding-bottom: ${oldTheme.spacing.xs};
`;

export const MathIntend = styled.div`
  padding-left: ${oldTheme.spacing.m};
  padding-top: ${oldTheme.spacing.xs};
  padding-bottom: ${oldTheme.spacing.xs};
`;
